import { useEffect, useState, useCallback } from "react";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Plus, CalendarDays } from "lucide-react";

const STATUS = {
  confirmed: { label: "Confirmado", cls: "bg-blue-500/15 text-blue-400" },
  completed: { label: "Concluído", cls: "bg-green-500/15 text-green-400" },
  cancelled: { label: "Cancelado", cls: "bg-red-500/15 text-red-400" },
};

export default function AdminAppointments() {
  const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
  const [date, setDate] = useState(today);
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [open, setOpen] = useState(false);
  const [slots, setSlots] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_phone: "", service_id: "", barber_id: "", date: today, start_time: "" });

  const load = useCallback(() => { api.get("/appointments", { params: { date } }).then((r) => setItems(r.data || [])); }, [date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data || [])); api.get("/barbers").then((r) => setBarbers(r.data || [])); }, []);

  useEffect(() => {
    if (form.service_id && form.barber_id && form.date) {
      api.get("/availability", { params: { barber_id: form.barber_id, date: form.date, service_id: form.service_id } })
        .then((r) => setSlots(r.data.slots || [])).catch(() => setSlots([]));
    }
  }, [form.service_id, form.barber_id, form.date]);

  const setStatus = async (id, status) => {
    try { await api.put(`/appointments/${id}`, { status }); toast.success("Atualizado"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const cancel = async (id) => {
    try { await api.delete(`/appointments/${id}`); toast.success("Cancelado"); load(); }
    catch { toast.error("Erro"); }
  };

  const create = async () => {
    try {
      await api.post("/appointments", form);
      toast.success("Agendamento criado");
      setOpen(false);
      setForm({ client_name: "", client_phone: "", service_id: "", barber_id: "", date, start_time: "" });
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white">Agendamentos</h1>
          <p className="text-zinc-400 mt-1">Gerencie os horários da barbearia.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <CalendarDays className="w-4 h-4 text-amber-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="pl-9 bg-zinc-900 border-zinc-700 w-44" data-testid="appt-date-filter" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-amber-400 to-yellow-600 text-black font-bold rounded-full" data-testid="admin-add-appointment-btn"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="text-white">Novo Agendamento</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-zinc-300">Nome do cliente</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="form-client-name" /></div>
                <div><Label className="text-zinc-300">Telefone</Label><Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="form-client-phone" /></div>
                <div><Label className="text-zinc-300">Serviço</Label>
                  <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="form-service"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} • {s.duration}min</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-300">Barbeiro</Label>
                  <Select value={form.barber_id} onValueChange={(v) => setForm({ ...form, barber_id: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="form-barber"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>{barbers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-300">Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 bg-zinc-800 border-zinc-700" data-testid="form-date" /></div>
                <div><Label className="text-zinc-300">Horário</Label>
                  <Select value={form.start_time} onValueChange={(v) => setForm({ ...form, start_time: v })}>
                    <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-700" data-testid="form-slot"><SelectValue placeholder={slots.length ? "Selecione" : "Sem horários"} /></SelectTrigger>
                    <SelectContent>{slots.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={!form.client_name || !form.service_id || !form.barber_id || !form.start_time} className="bg-amber-400 text-black font-bold" data-testid="form-save-appointment">Salvar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-zinc-900 border border-zinc-800 rounded-2xl text-zinc-500">Nenhum agendamento nesta data.</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => {
            const st = STATUS[a.status] || STATUS.confirmed;
            return (
              <div key={a.id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`admin-appt-${a.id}`}>
                <div className="flex items-center gap-4">
                  <div className="text-center w-16"><div className="text-amber-400 font-bold text-lg">{a.start_time}</div><div className="text-xs text-zinc-500">{a.end_time}</div></div>
                  <div>
                    <div className="text-white font-semibold">{a.client_name} <span className="text-zinc-500 text-sm font-normal">{a.client_phone}</span></div>
                    <div className="text-sm text-zinc-400">{a.service_name} • {a.barber_name} • <span className="text-amber-400">{money(a.price)}</span></div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={st.cls}>{st.label}</Badge>
                  {a.status !== "completed" && a.status !== "cancelled" && (
                    <Button size="sm" variant="outline" className="border-green-500/40 text-green-400 hover:bg-green-500/10" onClick={() => setStatus(a.id, "completed")} data-testid={`complete-${a.id}`}><CheckCircle2 className="w-4 h-4" /></Button>
                  )}
                  {a.status !== "cancelled" && (
                    <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10" onClick={() => cancel(a.id)} data-testid={`admin-cancel-${a.id}`}><XCircle className="w-4 h-4" /></Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
